// FacilitatorQuestionBankItemEditor
// Single-item editor for the per-type Question-bank library. Wraps
// QbankTemplateForm with metadata controls (category, difficulty,
// disciplines, status) and a save/publish/archive bar mirroring the
// existing mcq-exercise editor pattern.

import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import {
  emptyQuestionBankItem,
  normaliseQuestionBankItem,
  validateQuestionBankItem,
  QBANK_ITEM_TYPE_LABELS,
  QBANK_ITEM_TYPE_ICONS,
} from '../../lib/questionBankItems';
import { MCQ_DIFFICULTIES, DIFFICULTY_LABELS, QUESTION_DISCIPLINES } from '../../lib/mcq';
import QbankTemplateForm from './QbankTemplateForm';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const disciplineLabels = { medical: 'Medical', pharmacy: 'Pharmacy', nursing: 'Nursing' };

const FacilitatorQuestionBankItemEditor = ({
  initialItem,
  defaultType = 'mcq',
  onSave,
  onPublish,
  onArchive,
  onDelete,
  onBack,
}) => {
  const [item, setItem] = useState(() => normaliseQuestionBankItem(
    initialItem || emptyQuestionBankItem(defaultType)
  ));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialItem) setItem(normaliseQuestionBankItem(initialItem));
  }, [initialItem]);

  const issues = useMemo(() => validateQuestionBankItem(item), [item]);

  const updateItem = (patch) => setItem((current) => ({ ...current, ...patch }));
  const updatePayload = (nextPayload) => setItem((current) => ({ ...current, payload: nextPayload }));

  // Save (status untouched) or save-then-publish. Publishing an unsaved
  // item creates it first, then flips status — so the lecturer gets a
  // single click "Save and publish" path without having to round-trip
  // through "Save draft" → "Publish" manually.
  const handleSave = async ({ publish = false } = {}) => {
    if (issues.length || !onSave) return;
    setSaving(true);
    setError('');
    try {
      const saved = await onSave({
        templateType: item.templateType,
        payload: item.payload,
        category: item.category,
        difficulty: item.difficulty,
        disciplines: item.disciplines,
      });
      if (publish && onPublish) {
        const id = saved?.id || item.id;
        if (id) await onPublish(id);
        setMessage('Saved and published.');
      } else {
        setMessage('Saved.');
      }
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setError(err?.message || 'Could not save this question.');
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = QBANK_ITEM_TYPE_LABELS[item.templateType] || item.templateType;
  const typeIcon = QBANK_ITEM_TYPE_ICONS[item.templateType] || 'bi-question-square';

  return (
    <div className="student-page drill-editor-page">
      <Container className="mt-4">
        <FacilitatorPageHeader
          title={<>
            <i className={`bi ${typeIcon} me-2`} aria-hidden="true" />
            {item.id ? 'Edit question' : 'Create question'}
            <Badge bg="info" className="ms-2 align-middle">{typeLabel}</Badge>
          </>}
          description="Standalone question in the library. Save as draft while authoring; publish to make it available to drop into sets and curated revision exercises."
          onBack={onBack}
          backLabel="Back to library"
          actions={<>
            <Button type="button" variant="primary" size="sm"
              onClick={() => handleSave({ publish: false })}
              disabled={saving || issues.length > 0}>
              {saving ? 'Saving…' : (item.status === 'published' ? 'Save changes' : 'Save draft')}
            </Button>
            {item.status !== 'published' && onPublish ? (
              <Button type="button" variant="success" size="sm"
                onClick={() => handleSave({ publish: true })}
                disabled={saving || issues.length > 0}>
                {item.id ? 'Publish' : 'Save and publish'}
              </Button>
            ) : null}
            {item.id && item.status === 'published' && onArchive ? (
              <Button type="button" variant="outline-dark" size="sm" onClick={() => onArchive(item.id)}>Archive</Button>
            ) : null}
            {item.id && onDelete ? (
              <Button type="button" variant="outline-danger" size="sm" onClick={() => onDelete(item.id)}>Delete</Button>
            ) : null}
          </>}
        />

        {message ? <Alert variant="success">{message}</Alert> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {issues.length ? (
          <Alert variant="warning">
            <strong>Fix before saving:</strong>
            <ul className="mb-0">{issues.map((i) => <li key={i.key}>{i.message}</li>)}</ul>
          </Alert>
        ) : null}

        <Card className="drill-editor-card mb-3">
          <Card.Body>
            <Row className="g-3">
              <Form.Group as={Col} md={4}>
                <Form.Label>Category (topic tag)</Form.Label>
                <Form.Control
                  value={item.category}
                  onChange={(e) => updateItem({ category: e.target.value })}
                  placeholder="e.g. Antimicrobials, Anticoagulation, Pain"
                  maxLength={80}
                />
              </Form.Group>
              <Form.Group as={Col} md={4}>
                <Form.Label>Difficulty</Form.Label>
                <Form.Select value={item.difficulty} onChange={(e) => updateItem({ difficulty: e.target.value })}>
                  <option value="">— Not set —</option>
                  {MCQ_DIFFICULTIES.map((d) => (
                    <option key={d} value={d}>{DIFFICULTY_LABELS[d]}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md={4}>
                <Form.Label>Student-group tag (optional)</Form.Label>
                <div className="d-flex gap-3 flex-wrap pt-1">
                  {QUESTION_DISCIPLINES.map((d) => (
                    <Form.Check
                      key={d}
                      inline
                      type="checkbox"
                      id={`qbi-discipline-${d}`}
                      label={disciplineLabels[d]}
                      checked={item.disciplines.includes(d)}
                      onChange={(event) => {
                        const next = new Set(item.disciplines);
                        if (event.target.checked) next.add(d); else next.delete(d);
                        updateItem({ disciplines: Array.from(next) });
                      }}
                    />
                  ))}
                </div>
              </Form.Group>
            </Row>
          </Card.Body>
        </Card>

        <Card className="drill-editor-card mb-3">
          <Card.Body>
            <QbankTemplateForm
              template={item.payload}
              onChange={(next) => updatePayload(next)}
              idPrefix={`qbi-${item.id || 'new'}`}
            />
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default FacilitatorQuestionBankItemEditor;
